import { useState, useEffect } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logEvent } from '@/lib/monitoring';
import type { BootstrapCallbacks } from '../types';

export function useAuthBootstrap(callbacks: BootstrapCallbacks) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      console.log("[useAuthBootstrap] getSession resolved", { hasUser: !!s?.user });
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);

      if (!s?.user) {
        console.log("[useAuthBootstrap] signed-out path entered");
        void callbacks.onSignedOut();
        setIsAuthReady(true);
        return;
      }

      console.log("[useAuthBootstrap] hydration started", { userId: s.user.id });
      void callbacks.onHydrate(s.user.id, true);
    });

    // Auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        console.log("[useAuthBootstrap] auth state → signed-out");
        void callbacks.onSignedOut();
        setLoading(false);
        setIsAuthReady(true);
        return;
      }

      setLoading(false);
      if (event === 'SIGNED_IN') {
        setIsAuthReady(false);
        logEvent('LOGIN', 'User signed in', { userId: nextSession.user.id });
      }
      console.log("[useAuthBootstrap] hydration triggered", { event });
      void callbacks.onHydrate(
        nextSession.user.id,
        event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED',
      );
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { session, user, loading, isAuthReady, setIsAuthReady };
}
